import useDriftClient from 'src/hooks/useDriftClient';
import { useMemo, useState } from 'react';
import {
	FeeTierDisplayUnit,
	StakedTierSelectValue,
} from 'src/components/Modals/FeeTierModal/types';
import {
	FEE_TIER_DISPLAY_UNIT_OPTIONS,
	STAKED_TIERS,
	VOLUME_TIERS,
} from 'src/components/Modals/FeeTierModal/consts';
import useFeeTierTable from 'src/components/Modals/FeeTierModal/hooks/useFeeTierModalTable';
import useUserFeeTier from 'src/components/Modals/FeeTierModal/hooks/useUserFeeTier';
import useFeeTierHandlers from 'src/components/Modals/FeeTierModal/hooks/useFeeTierModalHandlers';
import { useUpdateEffect } from 'react-use';

export default function useFeeTierModal() {
	const driftClient = useDriftClient();

	const [displayUnit, setDisplayUnit] = useState<FeeTierDisplayUnit>(
		FEE_TIER_DISPLAY_UNIT_OPTIONS[0].value
	);

	const {
		userVolumeTierIndex,
		userStakedTierIndex,
		userCurrent30dVolume,
		userCurrentStakedAmount,
		isLoadingVolume,
	} = useUserFeeTier();

	const userCurrentStakedTier =
		STAKED_TIERS[userStakedTierIndex] ?? STAKED_TIERS[0];

	const [selectedStakedTier, setSelectedStakedTier] =
		useState<StakedTierSelectValue>({
			...userCurrentStakedTier,
			selectId: userCurrentStakedTier.discount,
		});

	// Update the selected staked tier discount when the user's staked tier changes
	// (e.g. if the user staked or if the data wasn't ready yet on the prev render.)
	useUpdateEffect(() => {
		setSelectedStakedTier({
			...userCurrentStakedTier,
			selectId: userCurrentStakedTier.discount,
		});
	}, [userCurrentStakedTier]);

	const stateAccount = driftClient?.getStateAccount();
	const allFeeTiers = stateAccount?.perpFeeStructure?.feeTiers;

	// There are several tiers in `allFeeTiers` that have the `feeNumerator` zero'd out.
	// We want to ignore those, otherwise we can't determine if the user is on the highest tier.
	const feeTiers = useMemo(() => {
		return allFeeTiers?.filter((feeTier) => Boolean(feeTier.feeNumerator));
	}, [allFeeTiers]);

	const isUserOnHighestVolumeTier =
		userVolumeTierIndex >= (feeTiers?.length ?? 0) - 1;

	const { rows } = useFeeTierTable({
		feeTiers,
		displayUnit,
		selectedStakedTierDiscount: selectedStakedTier.discount,
	});

	const {
		handleStakeClick,
		handleChangeDisplayUnit,
		handleChangeStakedTier,
		handleCloseModal,
	} = useFeeTierHandlers({
		setDisplayUnit,
		setSelectedStakedTier,
	});

	const userCurrentVolume = isLoadingVolume
		? '-'
		: userCurrent30dVolume.toNotional();
	// This may be out of bounds / undefined if the user is on the highest tier.
	// We could instead check `isUserOnHighestTier`, but this seems safer.
	const userNextTierVolume = VOLUME_TIERS[userVolumeTierIndex + 1]?.display;

	const progressBarPercentage = useMemo(() => {
		// We show the progress bar maxed out when the user is on the highest tier.
		if (isUserOnHighestVolumeTier) {
			return 100;
		}

		const current = userCurrent30dVolume?.toNum() ?? 0;
		const total = VOLUME_TIERS[userVolumeTierIndex + 1]?.amount ?? 0;

		if (!total) {
			return 0;
		}

		const percentage = (current / total) * 100;

		// We use `99` as the actual max otherwise the progress bar thumb styling will be off.
		return Math.min(Math.max(percentage, 0), 99);
	}, [isUserOnHighestVolumeTier, userCurrent30dVolume, userVolumeTierIndex]);

	return {
		feeTiers,
		userVolumeTierIndex,
		userCurrentVolume,
		userNextTierVolume,
		isUserOnHighestVolumeTier,
		userCurrentStakedAmount,
		userCurrentStakedTier,
		selectedStakedTier,
		displayUnit,
		tableRows: rows,
		progressBarPercentage,
		handleStakeClick,
		handleChangeDisplayUnit,
		handleChangeStakedTier,
		handleCloseModal,
	};
}
